import React, { useState, useEffect } from "react"; // Import useEffect
import {
	Typography,
	Switch,
	FormControlLabel,
	Box,
	Paper,
	CircularProgress,
	Alert,
	Snackbar,
} from "@mui/material";
import GppGoodIcon from "@mui/icons-material/GppGood";

import axios from "../../../api/axios"; // Your axios instance
import useAuth from "../../../hooks/useAuth";
import useAxiosPrivate from "../../../hooks/useAxiosPrivate";

const TradingDashboard = () => {
	// Remove isActive state - use accountMode from context
	const [loading, setLoading] = useState(false);
	const [data, setData] = useState(null);
	const [error, setError] = useState("");
	const [showSuccess, setShowSuccess] = useState(false); // For Snackbar
	const { auth, accountMode, setRealAccountMode, setDemoAccountMode } =
		useAuth(); // Get setDemoAccountMode
	const axiosPrivate = useAxiosPrivate();

	// Use useEffect to trigger API call when accountMode becomes 'real'
	useEffect(() => {
		console.log(accountMode);
		if (accountMode === "real" && auth?.id) {
			const fetchRealAccountData = async () => {
				setLoading(true);
				setError("");
				try {
					// Consider if this PATCH request should also fetch data
					// or if you need a separate GET request for real account data.
					const response = await axiosPrivate.patch(
						`/real-account/${auth?.id}`
					);
					setData(response.data);
					setShowSuccess(true); // Show success snackbar after successful activation and data fetch
				} catch (err) {
					setError("Failed to activate real trading account or fetch data.");
					// If activation fails, you might want to switch back to demo mode
					// setDemoAccountMode(); // Uncomment if this is the desired behavior
				} finally {
					setLoading(false);
				}
			};
			fetchRealAccountData();
		} else if (accountMode === "demo") {
			// Reset data when switching to demo mode
			setData(null);
			setShowSuccess(false); // Hide success snackbar when switching to demo
		}
	}, [accountMode, auth?.id, axiosPrivate]); // Depend on accountMode, auth.id, and axiosPrivate

	const handleToggle = () => {
		if (accountMode === "real") {
			// Switching to demo mode
			setDemoAccountMode();
		} else {
			// Switching to real mode
			setRealAccountMode();
			// The API call will be triggered by the useEffect when accountMode changes to 'real'
		}
	};

	const handleCloseSnackbar = () => {
		setShowSuccess(false);
	};

	return (
		<Box sx={{ maxWidth: 900, margin: "auto", padding: 2, my: "3rem" }}>
			{/* Toggle Switch */}
			<FormControlLabel
				control={
					<Switch
						checked={accountMode === "real"} // Check if in real mode
						onChange={handleToggle}
						color="primary"
						name="activateRealAccount"
						disabled={loading} // Disable switch while loading
					/>
				}
				label="Activate Real Trading Account"
				sx={{ mb: 3 }}
			/>

			{/* Success Snackbar */}
			<Snackbar
				open={showSuccess}
				autoHideDuration={3000}
				onClose={handleCloseSnackbar}
				anchorOrigin={{ vertical: "top", horizontal: "center" }}>
				<Alert
					severity="success"
					onClose={handleCloseSnackbar}
					sx={{ width: "100%" }}>
					🎉 Congratulations! You are now trading with your real account.
				</Alert>
			</Snackbar>

			{/* Content based on accountMode */}
			{accountMode === "real" ? (
				<>
					<Typography variant="h6" gutterBottom sx={{ color: "green" }}>
						Real Trading Account Activated
					</Typography>
					{loading ? (
						<CircularProgress />
					) : error ? (
						<Alert severity="error">{error}</Alert>
					) : data ? (
						// Display real account specific data here
						<Typography>
							Invest & grow your real funds. (Data available)
						</Typography>
					) : (
						<Typography>No real account data available.</Typography>
					)}
				</>
			) : (
				// Content for demo mode
				<Typography>
					Switch to activate your real trading account and invest & grow your
					real funds.
				</Typography>
			)}

			{/* Static info */}
			<Paper sx={{ p: 3 }}>
				<Typography variant="h4" gutterBottom>
					About Real Trading Accounts
				</Typography>
				<Typography paragraph>
					Opening a real trading account allows you to participate in live
					markets and manage actual funds. Unlike demo accounts, real accounts
					involve real money, which requires careful risk management and
					strategy.
				</Typography>
				<Typography paragraph>
					Before activating your real account, ensure you understand the risks
					involved. Our platform provides real-time data, advanced trading
					tools, and secure transaction capabilities to help you succeed.
				</Typography>
				<Typography paragraph>
					To activate your real trading account, toggle the switch at the top of
					this page. Once activated, you will be able to view your live account
					details, open positions, and manage your investments.
				</Typography>
			</Paper>
		</Box>
	);
};

export default TradingDashboard;
